import { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { EASE } from "@/components/motion-primitives";

// Auto-advancing photo carousel. Pauses on hover.
// Props: slides = [{ src, alt }], interval (ms), aspect (e.g. "aspect-[4/5]").
export const PhotoCarousel = ({ slides = [], interval = 2500, aspect = "aspect-[4/5]", testId = "photo-carousel" }) => {
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const [paused, setPaused] = useState(false);
  const containerRef = useRef(null);

  const goto = useCallback((next, dir = 1) => {
    setDirection(dir);
    setIndex(((next % slides.length) + slides.length) % slides.length);
  }, [slides.length]);

  const next = useCallback(() => goto(index + 1, 1), [goto, index]);
  const prev = useCallback(() => goto(index - 1, -1), [goto, index]);

  useEffect(() => {
    if (paused || slides.length <= 1) return undefined;
    const t = setTimeout(() => next(), interval);
    return () => clearTimeout(t);
  }, [index, paused, interval, next, slides.length]);

  // Keyboard nav when carousel is focused
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const onKey = (e) => {
      if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") prev();
    };
    el.addEventListener("keydown", onKey);
    return () => el.removeEventListener("keydown", onKey);
  }, [next, prev]);

  if (slides.length === 0) return null;

  return (
    <div
      ref={containerRef}
      className={`relative w-full ${aspect} overflow-hidden bg-secondary group`}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      tabIndex={0}
      data-testid={testId}
    >
      <AnimatePresence initial={false} custom={direction} mode="popLayout">
        <motion.img
          key={index}
          src={slides[index].src}
          alt={slides[index].alt}
          custom={direction}
          initial={{ opacity: 0, scale: 1.02, x: direction > 0 ? 24 : -24 }}
          animate={{ opacity: 1, scale: 1, x: 0 }}
          exit={{ opacity: 0, scale: 1.02, x: direction > 0 ? -24 : 24 }}
          transition={{ duration: 0.6, ease: EASE }}
          className="absolute inset-0 w-full h-full object-cover object-center"
          draggable={false}
        />
      </AnimatePresence>

      {/* Arrows */}
      <button
        type="button"
        onClick={prev}
        aria-label="Photo précédente"
        data-testid={`${testId}-prev`}
        className="absolute left-3 top-1/2 -translate-y-1/2 z-10 w-10 h-10 flex items-center justify-center bg-white/85 hover:bg-white text-ink backdrop-blur-sm shadow transition-all opacity-0 group-hover:opacity-100 focus:opacity-100"
      >
        <ChevronLeft size={20} />
      </button>
      <button
        type="button"
        onClick={next}
        aria-label="Photo suivante"
        data-testid={`${testId}-next`}
        className="absolute right-3 top-1/2 -translate-y-1/2 z-10 w-10 h-10 flex items-center justify-center bg-white/85 hover:bg-white text-ink backdrop-blur-sm shadow transition-all opacity-0 group-hover:opacity-100 focus:opacity-100"
      >
        <ChevronRight size={20} />
      </button>

      {/* Dots */}
      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-2 z-10">
        {slides.map((_, i) => (
          <button
            key={i}
            type="button"
            onClick={() => goto(i, i > index ? 1 : -1)}
            aria-label={`Aller à la photo ${i + 1}`}
            data-testid={`${testId}-dot-${i}`}
            className={`h-1.5 transition-all ${i === index ? "w-6 bg-white" : "w-1.5 bg-white/60 hover:bg-white/80"}`}
          />
        ))}
      </div>

      {/* Auto-advance progress bar */}
      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-white/20 z-10">
        <motion.div
          key={`${index}-${paused ? "p" : "r"}`}
          className="h-full bg-accentbrand"
          initial={{ width: "0%" }}
          animate={{ width: paused ? "0%" : "100%" }}
          transition={{ duration: paused ? 0 : interval / 1000, ease: "linear" }}
        />
      </div>
    </div>
  );
};
